import React, { useState } from "react";
import {
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Box, Typography, Divider,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../reduxStore/Store";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BadgeIcon from "@mui/icons-material/Badge";
import BarChartIcon from "@mui/icons-material/BarChart";
import BusinessIcon from "@mui/icons-material/Business";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SecurityIcon from "@mui/icons-material/Security";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArticleIcon from "@mui/icons-material/Article";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import TuneIcon from "@mui/icons-material/Tune";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BuildIcon from "@mui/icons-material/Build";
import { labels } from "../constants/labels";
import { isMultiDeptRoute } from "../constants/multiDeptRoutes";

const DRAWER_WIDTH = 272;

// Couleurs du thème sombre
const BG         = "rgba(10, 22, 45, 0.97)";
const ACTIVE_BG  = "rgba(255,255,255,0.12)";
const HOVER_BG   = "rgba(255,255,255,0.07)";
const ACTIVE_DOT = "#60a5fa";  // bleu clair

type NavItem    = { to: string; label: string | (() => string); icon: React.ReactNode; multiDeptOnly?: boolean; permission?: string };
type NavSection = { divider: true; section: string };
type MenuItem   = NavItem | NavSection;

const menuItems: MenuItem[] = [
  { to: "/",                    label: "Accueil",                       icon: <HomeIcon /> },
  { to: "/dashboard",           label: "Dashboard",                     icon: <DashboardIcon /> },
  { to: "/parametre_generique", label: "Paramètres génériques",         icon: <SettingsIcon /> },
  { to: "/options",             label: "Options personnalisables",      icon: <TuneIcon /> ,               multiDeptOnly: true },
  { to: "/aiTokens",            label: "Tokens IA (MCP)",                icon: <SmartToyIcon />,            multiDeptOnly: true },
  { to: "/departement",         label: "Départements",                  icon: <BusinessIcon />,            multiDeptOnly: true },

  { divider: true, section: "Gestion" },
  { to: "/pos",                 label: "Point de vente",                icon: <PointOfSaleIcon /> },
  { to: "/facturation",         label: "Documents",                     icon: <ReceiptIcon /> },
  { to: "/article",             label: () => labels.gestionArticles,    icon: <Inventory2Icon /> },
  { to: "/article/masse",       label: "Actions par famille",           icon: <AutoFixHighIcon /> },
  { to: "/comptes",             label: "Comptes & Balances",            icon: <AccountBalanceIcon /> },
  { to: "/caisse",              label: () => labels.gestionCaisse,      icon: <AccountBalanceWalletIcon /> },
  { to: "/operationsDiverses",  label: "Opérations diverses",           icon: <SwapHorizIcon /> },
  { to: "/StockAndStat",        label: "Stock & Statistiques",          icon: <BarChartIcon /> },
  { to: "/immobilisation",      label: "Immobilisations",               icon: <BuildIcon /> },

  { divider: true, section: "Tiers" },
  { to: "/client",              label: () => labels.clientPluriel,      icon: <PeopleIcon />,             multiDeptOnly: true },
  { to: "/fournisseur",         label: () => labels.fournisseurPluriel, icon: <PersonIcon />,             multiDeptOnly: true },
  { to: "/employe",             label: () => labels.employePluriel,     icon: <BadgeIcon />,              multiDeptOnly: true },

  { divider: true, section: "Administration" },
  { to: "/utilisateurs",        label: "Utilisateurs",                  icon: <PeopleIcon />,              multiDeptOnly: true, permission: "users.read" },
  { to: "/roles",               label: "Rôles",                         icon: <SecurityIcon />,            multiDeptOnly: true, permission: "roles.read" },
  { to: "/user-droits",         label: "Droits utilisateurs",           icon: <AdminPanelSettingsIcon />, multiDeptOnly: true, permission: "roles.read" },
  { to: "/workflows",           label: "Workflows documents",           icon: <AccountTreeIcon />,        multiDeptOnly: true },
  { to: "/workflow-statuts",    label: "Statuts de documents",          icon: <AccountTreeIcon />,        multiDeptOnly: true },


 
  { divider: true, section: "Rapports" },
  { to: "/rapports",            label: "Générer un rapport",            icon: <AssessmentIcon />,          multiDeptOnly: true },
  { to: "/reportTemplates",     label: "Templates de rapports",         icon: <ArticleIcon />,          multiDeptOnly: true    },
];

const DrawerMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { isAllMode } = useSelector((s: RootState) => s.AppSlice);
  const { permissions } = useSelector((s: RootState) => s.AuthSlice);
  const allowedDepartements = useSelector((s: RootState) => s.ScopeSlice.allowedDepartements);
  const isMonoDepartement = allowedDepartements.length === 1;

  const toggleDrawer = () => setOpen((prev) => !prev);

  // Filtre selon le mode actif :
  //   - Mono-département (un seul dept autorisé) : tout est accessible, la
  //     distinction dept/ALL est artificielle avec un seul département —
  //     cf. AllModeRequiredRoute.
  //   - Mode "Tous départements" : garde uniquement les items compatibles ALL
  //     (MULTI_DEPT_ROUTES) + ceux marqués `multiDeptOnly` (ex: roles, droits user
  //     — gestion tenant-level qui ne s'expose qu'en vue globale).
  //   - Mode dept : garde tout SAUF les items `multiDeptOnly` (cachés en dept).
  // Masque aussi les items dont la `permission` manque (l'API reste le vrai verrou).
  // Puis retire les dividers de section devenus orphelins.
  const visibleItems: MenuItem[] = (() => {
    const filtered = menuItems.filter((it) => {
      if ('divider' in it) return true;
      if (it.permission && !permissions.includes(it.permission)) return false;
      if (isMonoDepartement) return true;
      if (isAllMode) return isMultiDeptRoute(it.to) || it.multiDeptOnly === true;
      return it.multiDeptOnly !== true;
    });
    return filtered.filter((it, i) => {
      if (!('divider' in it)) return true;
      for (let j = i + 1; j < filtered.length; j++) {
        const next = filtered[j];
        if ('divider' in next) return false;
        return true;
      }
      return false;
    });
  })();

  return (
    <Box sx={{ display: "flex" }}>
      <IconButton color="inherit" aria-label="open drawer" onClick={toggleDrawer} edge="start">
        <MenuIcon />
      </IconButton>

      <Drawer
        variant="temporary"
        anchor="left"
        open={open}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            background: BG,
            backdropFilter: "blur(20px)",
            border: "none",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "8px 0 32px rgba(0,0,0,0.45)",
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(2px)",
            backgroundColor: "rgba(0,0,0,0.4)",
          },
        }}
      >
        {/* ── Logo ── */}
        <Box sx={{ px: 2.5, pt: 3, pb: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40, height: 40,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 14px rgba(59,130,246,0.45)",
            }}
          >
            <ApartmentIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1} color="white">
              Gsoft Web
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem" }}>
              ERP Platform
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2, mb: 1 }} />

        {/* ── Navigation ── */}
        <List sx={{ px: 1.5, py: 0.5, flex: 1, overflowY: "auto" }}>
          {visibleItems.map((item, i) => {
            if ("divider" in item) {
              return (
                <Box key={i} sx={{ mt: 2.5, mb: 0.5, px: 1.5 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: "rgba(255,255,255,0.3)",
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                    }}
                  >
                    {item.section}
                  </Typography>
                </Box>
              );
            }

            const label    = typeof item.label === "function" ? item.label() : item.label;
            const isActive = location.pathname === item.to;

            return (
              <ListItemButton
                key={item.to}
                component={Link}
                to={item.to}
                onClick={toggleDrawer}
                sx={{
                  borderRadius: "10px",
                  mb: 0.5,
                  px: 1.5,
                  py: 0.85,
                  color: isActive ? "white" : "rgba(255,255,255,0.55)",
                  bgcolor: isActive ? ACTIVE_BG : "transparent",
                  position: "relative",
                  transition: "all 0.15s ease",
                  "&::before": isActive ? {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "20%",
                    height: "60%",
                    width: 3,
                    borderRadius: "0 3px 3px 0",
                    background: ACTIVE_DOT,
                    boxShadow: `0 0 8px ${ACTIVE_DOT}`,
                  } : {},
                  "&:hover": {
                    bgcolor: HOVER_BG,
                    color: "rgba(255,255,255,0.9)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: isActive ? ACTIVE_DOT : "rgba(255,255,255,0.35)",
                    "& .MuiSvgIcon-root": { fontSize: 19 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: "0.855rem",
                    fontWeight: isActive ? 600 : 400,
                    noWrap: true,
                  }}
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 6, height: 6,
                      borderRadius: "50%",
                      bgcolor: ACTIVE_DOT,
                      boxShadow: `0 0 6px ${ACTIVE_DOT}`,
                      flexShrink: 0,
                    }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>

        {/* ── Pied ── */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 1.5 }} />
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.7rem" }}>
            v1.0.0 · Gsoft Web ERP
          </Typography>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DrawerMenu;
